import { useEffect, useMemo, useRef, useState } from "react";
import AdminSidebarShell from "../components/AdminSidebarShell";
import config from "../config";

const SESSION_STORAGE_KEY = "adminDocumentChatSessionId";
const ACTIVE_DOCUMENT_STORAGE_KEY = "adminDocumentActiveDocumentId";
const MIN_RECORDING_MS = 1000;
const STOP_BUFFER_MS = 700;

async function parseJsonResponse(response) {
  const raw = await response.text();
  try {
    return raw ? JSON.parse(raw) : {};
  } catch {
    throw new Error("Server returned a non-JSON response");
  }
}

function parseSseEvents(chunk, onEvent) {
  const events = chunk.split("\n\n");
  const tail = events.pop() || "";

  for (const event of events) {
    const line = event
      .split("\n")
      .find((value) => value.startsWith("data:"));

    if (!line) continue;
    const payloadRaw = line.replace(/^data:\s*/, "").trim();
    if (!payloadRaw) continue;

    try {
      const payload = JSON.parse(payloadRaw);
      onEvent(payload);
    } catch {
      // Ignore malformed chunked events.
    }
  }

  return tail;
}

const AdminQueryDocuments = () => {
  const token = useMemo(() => localStorage.getItem("token"), []);
  const apiBase = config.API_BASE_URL || "http://localhost:5003";
  const [sessionId, setSessionId] = useState("");
  const [documents, setDocuments] = useState([]);
  const [selectedDocumentId, setSelectedDocumentId] = useState("");
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [isSending, setIsSending] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [voiceEnabled, setVoiceEnabled] = useState(true);
  const [autoSubmitVoice, setAutoSubmitVoice] = useState(false);
  const [status, setStatus] = useState("");
  const [error, setError] = useState("");
  const endRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const audioUrlRef = useRef("");
  const stopTimeoutRef = useRef(null);
  const recordingStartedAtRef = useRef(0);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [messages]);

  const syncSession = async (sessionToUse) => {
    const response = await fetch(
      `${apiBase}${config.ADMIN.DOCUMENT_CHAT_MESSAGES(sessionToUse)}?limit=10`,
      {
        headers: {
          Authorization: `Bearer ${token}`
        }
      }
    );

    const payload = await parseJsonResponse(response);
    if (!response.ok) {
      throw new Error(payload?.error || "Failed to load chat history");
    }

    const safeMessages = Array.isArray(payload?.messages)
      ? payload.messages.map((item) => ({
          id: item.id,
          role: item.role,
          message: item.message,
          responseTimeMs: item.responseTimeMs || null
        }))
      : [];

    setMessages(safeMessages);
    setSessionId(sessionToUse);
    localStorage.setItem(SESSION_STORAGE_KEY, sessionToUse);
  };

  const loadDocuments = async () => {
    const response = await fetch(`${apiBase}${config.ADMIN.DOCUMENT_LIST}`, {
      headers: {
        Authorization: `Bearer ${token}`
      }
    });

    const payload = await parseJsonResponse(response);
    if (!response.ok) {
      throw new Error(payload?.error || "Failed to load documents");
    }

    const docs = Array.isArray(payload) ? payload : [];
    setDocuments(docs);

    const savedDocumentId = String(localStorage.getItem(ACTIVE_DOCUMENT_STORAGE_KEY) || "").trim();
    const availableIds = new Set(docs.map((item) => String(item?.id || "").trim()).filter(Boolean));

    const nextDocumentId = availableIds.has(savedDocumentId)
      ? savedDocumentId
      : String(docs[0]?.id || "").trim();

    setSelectedDocumentId(nextDocumentId);
    if (nextDocumentId) {
      localStorage.setItem(ACTIVE_DOCUMENT_STORAGE_KEY, nextDocumentId);
    } else {
      localStorage.removeItem(ACTIVE_DOCUMENT_STORAGE_KEY);
    }
  };

  const createSession = async (requestedSessionId = "") => {
    const response = await fetch(`${apiBase}${config.ADMIN.DOCUMENT_CHAT_SESSION}`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify(
        requestedSessionId
          ? { sessionId: requestedSessionId }
          : {}
      )
    });

    const payload = await parseJsonResponse(response);
    if (!response.ok) {
      throw new Error(payload?.error || "Failed to initialize chat session");
    }

    const createdSessionId = String(payload?.sessionId || "").trim();
    if (!createdSessionId) {
      throw new Error("Server did not return a valid session ID");
    }

    await syncSession(createdSessionId);
    return createdSessionId;
  };

  useEffect(() => {
    if (!token) return;

    let mounted = true;

    (async () => {
      try {
        setError("");
        setStatus("Preparing chat...");
        await loadDocuments();
        const savedSession = String(localStorage.getItem(SESSION_STORAGE_KEY) || "").trim();
        const sessionToUse = savedSession || (await createSession());

        if (savedSession) {
          await syncSession(sessionToUse);
        }

        if (!mounted) return;
        setStatus("");
      } catch (err) {
        if (!mounted) return;
        setStatus("");
        setError(err.message || "Failed to initialize chat");
      }
    })();

    return () => {
      mounted = false;
    };
  }, [apiBase, token]);

  const setAssistantContent = (assistantMessageId, updater) => {
    setMessages((prev) =>
      prev.map((msg) => {
        if (msg.id !== assistantMessageId) return msg;
        return {
          ...msg,
          message: typeof updater === "function" ? updater(msg.message) : updater
        };
      })
    );
  };

  const setAssistantResponseTime = (assistantMessageId, responseTimeMs) => {
    const value = Number(responseTimeMs);
    setMessages((prev) =>
      prev.map((msg) =>
        msg.id === assistantMessageId
          ? { ...msg, responseTimeMs: Number.isFinite(value) ? value : null }
          : msg
      )
    );
  };

  const stopPlayback = () => {
    if (audioUrlRef.current) {
      URL.revokeObjectURL(audioUrlRef.current);
      audioUrlRef.current = "";
    }
    if (typeof window !== "undefined" && "speechSynthesis" in window) {
      window.speechSynthesis.cancel();
    }
  };

  const playVoiceAnswer = async (answer) => {
    const cleanAnswer = String(answer || "").trim();
    if (!voiceEnabled || !cleanAnswer || !token) return;

    stopPlayback();

    try {
      const response = await fetch(`${apiBase}${config.ADMIN.TEXT_TO_SPEECH}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ text: cleanAnswer })
      });

      const contentType = response.headers.get("content-type") || "";
      if (!response.ok) {
        throw new Error("Voice playback unavailable");
      }

      if (contentType.includes("audio/")) {
        const blob = await response.blob();
        const objectUrl = URL.createObjectURL(blob);
        audioUrlRef.current = objectUrl;
        const audio = new Audio(objectUrl);
        void audio.play().catch(() => {});
        return;
      }

      const payload = await parseJsonResponse(response);
      const fallbackText = String(payload?.text || cleanAnswer).trim();
      if (
        fallbackText &&
        typeof window !== "undefined" &&
        "speechSynthesis" in window
      ) {
        const utterance = new SpeechSynthesisUtterance(fallbackText);
        utterance.rate = 1;
        utterance.pitch = 1;
        window.speechSynthesis.speak(utterance);
      }
    } catch {
      if (typeof window !== "undefined" && "speechSynthesis" in window) {
        const utterance = new SpeechSynthesisUtterance(cleanAnswer);
        window.speechSynthesis.speak(utterance);
      }
    }
  };

  const handleAsk = async (messageOverride = "") => {
    const cleanInput = String(messageOverride || input).trim();
    if (!cleanInput || !token || !sessionId || isSending) return;
    if (!selectedDocumentId) {
      setError("Select a document before asking a question.");
      return;
    }

    const userId = `u-${Date.now()}`;
    const assistantId = `a-${Date.now()}`;

    setError("");
    setStatus("");
    setIsSending(true);
    setInput("");

    setMessages((prev) => [
      ...prev,
      { id: userId, role: "user", message: cleanInput },
      { id: assistantId, role: "assistant", message: "AI is typing...", responseTimeMs: null }
    ]);

    try {
      const response = await fetch(`${apiBase}${config.ADMIN.DOCUMENT_QUERY}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          sessionId,
          message: cleanInput,
          documentId: selectedDocumentId
        })
      });

      const contentType = response.headers.get("content-type") || "";

      if (!response.ok) {
        const payload = contentType.includes("application/json")
          ? await parseJsonResponse(response)
          : { error: await response.text() };
        throw new Error(payload?.error || "Failed to query documents");
      }

      if (contentType.includes("application/json")) {
        const payload = await parseJsonResponse(response);
        const answer = String(payload?.answer || "No answer generated.");
        setAssistantContent(assistantId, answer);
        if (payload?.responseTimeMs) {
          setAssistantResponseTime(assistantId, payload.responseTimeMs);
        }
        await playVoiceAnswer(answer);
        return;
      }

      const reader = response.body?.getReader();
      if (!reader) {
        throw new Error("Streaming response unavailable");
      }

      const decoder = new TextDecoder();
      const startTime = Date.now();
      let buffer = "";
      let receivedToken = false;
      let firstChunk = false;

      while (true) {
        const { done, value } = await reader.read();
        if (!firstChunk && value && value.length > 0) {
          const ttft = Date.now() - startTime;
          console.log("[frontend] ttft", ttft);
          firstChunk = true;
        }
        buffer += decoder.decode(value || new Uint8Array(), { stream: !done });

        buffer = parseSseEvents(buffer, (payload) => {
          if (payload?.sessionId) {
            const nextSessionId = String(payload.sessionId || "").trim();
            if (nextSessionId) {
              setSessionId(nextSessionId);
              localStorage.setItem(SESSION_STORAGE_KEY, nextSessionId);
            }
          }

          if (payload?.token) {
            const tokenValue = String(payload.token || "");
            if (!tokenValue) return;
            receivedToken = true;
            setAssistantContent(assistantId, (existing) =>
              existing === "AI is typing..." ? tokenValue : `${existing}${tokenValue}`
            );
          }

          if (payload?.message && payload?.done) {
            setAssistantContent(assistantId, payload.message);
            if (payload?.responseTimeMs) {
              setAssistantResponseTime(assistantId, payload.responseTimeMs);
            }
            void playVoiceAnswer(payload.message);
          }

          if (payload?.error) {
            throw new Error(payload.error);
          }
        });

        if (done) {
          break;
        }
      }

      if (!receivedToken) {
        setAssistantContent(assistantId, (existing) => existing || "No answer generated.");
      }
    } catch (err) {
      setMessages((prev) => prev.filter((msg) => msg.id !== assistantId));
      setError(err.message || "Failed to query documents");
    } finally {
      setIsSending(false);
    }
  };

  const clearChat = async () => {
    if (!sessionId || !token) return;
    try {
      setError("");
      setStatus("Clearing chat...");
      const response = await fetch(
        `${apiBase}${config.ADMIN.DOCUMENT_CHAT_MESSAGES(sessionId)}`,
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );

      const payload = await parseJsonResponse(response);
      if (!response.ok) {
        throw new Error(payload?.error || "Failed to clear chat");
      }

      setMessages([]);
      setStatus("Chat cleared.");
    } catch (err) {
      setError(err.message || "Failed to clear chat");
      setStatus("");
    }
  };

  const stopRecording = () => {
    if (stopTimeoutRef.current) {
      clearTimeout(stopTimeoutRef.current);
    }

    const recorder = mediaRecorderRef.current;
    if (recorder && recorder.state !== "inactive") {
      setStatus("Finishing recording...");
      stopTimeoutRef.current = setTimeout(() => {
        recorder.stop();
        stopTimeoutRef.current = null;
      }, STOP_BUFFER_MS);
      return;
    }
  };

  const startRecording = async () => {
    if (isRecording || isTranscribing || isSending) return;

    if (
      typeof navigator === "undefined" ||
      !navigator.mediaDevices?.getUserMedia ||
      typeof MediaRecorder === "undefined"
    ) {
      setError("Voice input is not supported on this device");
      return;
    }

    try {
      setError("");
      setStatus("Listening...");
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          sampleRate: 16000,
          channelCount: 1,
          noiseSuppression: true,
          echoCancellation: true
        }
      });
      const preferredMimeType = typeof MediaRecorder.isTypeSupported === "function" &&
        MediaRecorder.isTypeSupported("audio/webm;codecs=opus")
        ? "audio/webm;codecs=opus"
        : "";
      const recorder = preferredMimeType
        ? new MediaRecorder(stream, {
            mimeType: preferredMimeType,
            audioBitsPerSecond: 128000
          })
        : new MediaRecorder(stream);
      mediaRecorderRef.current = recorder;
      audioChunksRef.current = [];
      recordingStartedAtRef.current = Date.now();

      recorder.ondataavailable = (event) => {
        if (event.data?.size) {
          audioChunksRef.current.push(event.data);
        }
      };

      recorder.onstop = async () => {
        setIsRecording(false);
        const recordingDuration = Date.now() - recordingStartedAtRef.current;

        if (recordingDuration < MIN_RECORDING_MS) {
          audioChunksRef.current = [];
          setStatus("");
          setError("Please hold the mic a little longer and speak a full sentence.");
          stream.getTracks().forEach((track) => track.stop());
          mediaRecorderRef.current = null;
          return;
        }

        setIsTranscribing(true);
        setStatus("Transcribing...");

        try {
          const chunks = [...audioChunksRef.current];
          audioChunksRef.current = [];

          const audioBlob = new Blob(chunks, {
            type: recorder.mimeType || "audio/webm"
          });
          const formData = new FormData();
          formData.append("audio", audioBlob, "voice-query.webm");

          const response = await fetch(`${apiBase}${config.ADMIN.SPEECH_TO_TEXT}`, {
            method: "POST",
            headers: {
              Authorization: `Bearer ${token}`
            },
            body: formData
          });

          const payload = await parseJsonResponse(response);
          if (!response.ok) {
            throw new Error(payload?.error || "Failed to transcribe audio");
          }

          const transcribedText = String(payload?.text || "").trim();
          setInput(transcribedText);
          setStatus(transcribedText ? "Voice query ready." : "");

          if (transcribedText && autoSubmitVoice) {
            await handleAsk(transcribedText);
          }
        } catch (err) {
          setError(err.message || "Failed to transcribe audio");
          setStatus("");
        } finally {
          stream.getTracks().forEach((track) => track.stop());
          setIsTranscribing(false);
          mediaRecorderRef.current = null;
          recordingStartedAtRef.current = 0;
          if (stopTimeoutRef.current) {
            clearTimeout(stopTimeoutRef.current);
            stopTimeoutRef.current = null;
          }
        }
      };

      recorder.start(250);
      setIsRecording(true);
    } catch (err) {
      setError(err.message || "Unable to access microphone");
      setStatus("");
    }
  };

  useEffect(() => () => {
    if (stopTimeoutRef.current) {
      clearTimeout(stopTimeoutRef.current);
    }
    stopPlayback();
  }, []);

  const handleDocumentChange = (event) => {
    const nextDocumentId = String(event.target.value || "").trim();
    setSelectedDocumentId(nextDocumentId);
    if (nextDocumentId) {
      localStorage.setItem(ACTIVE_DOCUMENT_STORAGE_KEY, nextDocumentId);
    } else {
      localStorage.removeItem(ACTIVE_DOCUMENT_STORAGE_KEY);
    }
  };

  return (
    <AdminSidebarShell activeItem="query">
      <div style={contentWrapper}>
        <div style={header}>
          <h1 style={title}>Document Chat</h1>
          <p style={subtitle}>Ask anything from your uploaded documents and get a live answer as the Vault responds.</p>
        </div>

        <div style={chatShell}>
          <div style={chatTopBar}>
            <div style={sessionMeta}>
              <span style={sessionLabel}>Vault Session</span>
              <span style={sessionText}>{sessionId || "Initializing..."}</span>
            </div>
            <div style={topActions}>
              <select
                style={documentSelect}
                value={selectedDocumentId}
                onChange={handleDocumentChange}
                disabled={!documents.length || isSending}
              >
                {!documents.length ? (
                  <option value="">No documents uploaded</option>
                ) : null}
                {documents.map((doc) => (
                  <option key={doc.id} value={doc.id}>
                    {doc.fileName || `Document ${doc.id}`}
                  </option>
                ))}
              </select>
              <label style={togglePill}>
                <input
                  type="checkbox"
                  checked={voiceEnabled}
                  onChange={(event) => setVoiceEnabled(event.target.checked)}
                />
                <span>Voice {voiceEnabled ? "ON" : "OFF"}</span>
              </label>
              <button
                type="button"
                style={clearButton}
                onClick={clearChat}
                disabled={isSending || !sessionId}
              >
                Clear Chat
              </button>
            </div>
          </div>

          <div style={messagesPanel}>
            {messages.length ? (
              messages.map((item) => (
                <div
                  key={item.id}
                  style={{
                    ...messageBubble,
                    ...(item.role === "user" ? userBubble : assistantBubble)
                  }}
                >
                  <span style={messageRole}>
                    {item.role === "user" ? "You" : "Vault"}
                  </span>
                  <p style={messageText}>{item.message}</p>
                  {item.role === "assistant" && item.responseTimeMs ? (
                    <span style={messageMeta}>
                      Response time: {Math.round(item.responseTimeMs)} ms
                    </span>
                  ) : null}
                </div>
              ))
            ) : (
              <div style={emptyState}>
                <div style={emptyStateInner}>
                  <h2 style={emptyTitle}>Ask your documents anything</h2>
                  <p style={emptySubtitle}>
                    Try a direct question and the answer will stream in as soon as it starts.
                  </p>
                </div>
              </div>
            )}
            <div ref={endRef} />
          </div>

          <div style={composerDock}>
            <div style={composer}>
              <textarea
                style={textArea}
                value={input}
                onChange={(event) => setInput(event.target.value)}
                placeholder="Ask about your documents..."
                rows={2}
              />

              <div style={composerFooter}>
                <div style={voiceControls}>
                  <span style={composerHint}>
                    {isRecording
                      ? "Listening..."
                      : isTranscribing
                        ? "Transcribing..."
                        : "Ask about names, summaries, instructions, dates, or specific facts in your files."}
                  </span>
                  <label style={toggleMini}>
                    <input
                      type="checkbox"
                      checked={autoSubmitVoice}
                      onChange={(event) => setAutoSubmitVoice(event.target.checked)}
                    />
                    <span>Auto-send voice</span>
                  </label>
                </div>
                <div style={composerActions}>
                  <button
                    type="button"
                    style={{
                      ...micButton,
                      ...(isRecording ? micButtonActive : null)
                    }}
                    onClick={isRecording ? stopRecording : startRecording}
                    disabled={isTranscribing || isSending || !sessionId}
                    title={isRecording ? "Stop recording" : "Start voice query"}
                  >
                    {isRecording ? "Stop" : "Mic"}
                  </button>
                  <button
                    type="button"
                    style={actionButton}
                    onClick={() => handleAsk()}
                    disabled={!input.trim() || isSending || !sessionId || !selectedDocumentId}
                  >
                    {isSending ? "Thinking..." : "Send"}
                  </button>
                </div>
              </div>
            </div>
          </div>

          {status ? <p style={statusText}>{status}</p> : null}
          {error ? <p style={errorText}>{error}</p> : null}
        </div>
      </div>
    </AdminSidebarShell>
  );
};

export default AdminQueryDocuments;

const contentWrapper = {
  maxWidth: "980px",
  margin: "0 auto",
  fontFamily: "system-ui, -apple-system, sans-serif"
};

const header = {
  marginBottom: "24px"
};

const title = {
  fontSize: "28px",
  fontWeight: "700",
  color: "#1e293b",
  margin: "0 0 8px 0"
};

const subtitle = {
  fontSize: "15px",
  color: "#64748b",
  margin: 0
};

const chatShell = {
  background: "#ffffff",
  padding: "22px",
  borderRadius: "22px",
  boxShadow: "0 20px 42px rgba(14,85,128,0.10)",
  border: "1px solid #dbe5ee",
  display: "grid",
  gap: "16px",
  backgroundImage:
    "radial-gradient(circle at top, rgba(14,85,128,0.05), transparent 34%)"
};

const chatTopBar = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  gap: "12px",
  flexWrap: "wrap"
};

const topActions = {
  display: "flex",
  alignItems: "center",
  gap: "10px",
  flexWrap: "wrap"
};

const documentSelect = {
  minHeight: "42px",
  minWidth: "220px",
  padding: "0 10px",
  borderRadius: "10px",
  border: "1px solid #cbd5e1",
  background: "#ffffff",
  color: "#1e293b",
  fontSize: "13px",
  fontWeight: "600"
};

const sessionMeta = {
  display: "grid",
  gap: "3px"
};

const sessionLabel = {
  fontSize: "12px",
  color: "#64748b",
  fontWeight: "700",
  textTransform: "uppercase",
  letterSpacing: "0.06em"
};

const sessionText = {
  fontSize: "14px",
  color: "#334155",
  fontWeight: "600"
};

const clearButton = {
  border: "1px solid #cbd5e1",
  borderRadius: "10px",
  minHeight: "42px",
  padding: "0 14px",
  background: "#f8fafc",
  color: "#334155",
  fontSize: "14px",
  fontWeight: "600",
  cursor: "pointer"
};

const togglePill = {
  display: "inline-flex",
  alignItems: "center",
  gap: "8px",
  padding: "8px 12px",
  borderRadius: "999px",
  border: "1px solid #dbe5ee",
  background: "#f8fbfe",
  color: "#334155",
  fontSize: "13px",
  fontWeight: "600"
};

const messagesPanel = {
  minHeight: "440px",
  maxHeight: "60vh",
  overflowY: "auto",
  display: "grid",
  gap: "14px",
  padding: "8px 6px"
};

const messageBubble = {
  maxWidth: "85%",
  borderRadius: "18px",
  padding: "15px 17px",
  display: "grid",
  gap: "6px"
};

const userBubble = {
  justifySelf: "end",
  background: "#0E5580",
  color: "#fff"
};

const assistantBubble = {
  justifySelf: "start",
  background: "#fbfdff",
  color: "#1e293b",
  border: "1px solid #e2e8f0",
  boxShadow: "0 8px 20px rgba(15,23,42,0.04)"
};

const messageRole = {
  fontSize: "12px",
  fontWeight: "700",
  opacity: 0.8,
  textTransform: "uppercase",
  letterSpacing: "0.04em"
};

const messageText = {
  margin: 0,
  lineHeight: 1.6,
  whiteSpace: "pre-wrap"
};

const messageMeta = {
  fontSize: "12px",
  color: "#64748b"
};

const emptyState = {
  minHeight: "320px",
  display: "grid",
  placeItems: "center",
  border: "1px dashed #dbe5ee",
  borderRadius: "20px",
  background: "linear-gradient(180deg, #fcfeff 0%, #f7fbfe 100%)"
};

const emptyStateInner = {
  textAlign: "center",
  maxWidth: "480px",
  padding: "0 18px"
};

const emptyTitle = {
  margin: 0,
  color: "#0f172a",
  fontSize: "26px",
  fontWeight: "700"
};

const emptySubtitle = {
  margin: "10px 0 0",
  color: "#64748b",
  fontSize: "15px",
  lineHeight: 1.6
};

const composerDock = {
  position: "sticky",
  bottom: 0,
  paddingTop: "4px",
  background:
    "linear-gradient(180deg, rgba(255,255,255,0) 0%, rgba(255,255,255,0.92) 22%, rgba(255,255,255,1) 100%)"
};

const composer = {
  display: "grid",
  gap: "10px",
  padding: "12px 14px",
  borderRadius: "16px",
  border: "1px solid #dbe5ee",
  background: "#ffffff",
  boxShadow: "0 10px 24px rgba(15,23,42,0.05)"
};

const composerFooter = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  gap: "12px",
  flexWrap: "wrap"
};

const voiceControls = {
  display: "grid",
  gap: "8px",
  flex: "1 1 280px"
};

const composerHint = {
  fontSize: "12px",
  color: "#64748b",
  lineHeight: 1.5
};

const toggleMini = {
  display: "inline-flex",
  alignItems: "center",
  gap: "8px",
  color: "#475569",
  fontSize: "12px",
  fontWeight: "600"
};

const textArea = {
  border: "none",
  borderRadius: "12px",
  padding: "2px",
  fontSize: "15px",
  color: "#1e293b",
  resize: "vertical",
  minHeight: "52px",
  maxHeight: "120px",
  outline: "none",
  background: "transparent"
};

const composerActions = {
  display: "flex",
  alignItems: "center",
  gap: "10px",
  flexWrap: "wrap"
};

const micButton = {
  border: "1px solid #dbe5ee",
  borderRadius: "10px",
  minHeight: "42px",
  padding: "10px 14px",
  background: "#ffffff",
  color: "#0E5580",
  fontSize: "14px",
  fontWeight: "700",
  cursor: "pointer",
  minWidth: "84px"
};

const micButtonActive = {
  border: "1px solid #ef4444",
  background: "#fef2f2",
  color: "#b91c1c"
};

const actionButton = {
  border: "none",
  borderRadius: "10px",
  minHeight: "42px",
  padding: "10px 14px",
  background: "#0E5580",
  color: "#fff",
  fontSize: "14px",
  fontWeight: 700,
  cursor: "pointer",
  justifySelf: "start",
  minWidth: "96px"
};

const errorText = {
  margin: 0,
  color: "#b91c1c",
  fontSize: "13px"
};

const statusText = {
  margin: 0,
  color: "#334155",
  fontSize: "13px"
};
